/**
 * The gold set and regression gate (spec §2 I): before a learned rule enters
 * an estimating agent's prompt, ask the agent again on recent decisions people
 * already accepted — once with today's rules, once with the draft added — and
 * let the rule through only if it does not make the agent agree with people
 * less often. Preview runs write nothing; each counts as a model call.
 */

import { getSupabaseClient } from './supabaseClient';
import type { AgentSettings } from './agentSettingsService';
import { loadLearnedRules, approvedRuleTexts } from './agentLearnedRulesService';
import { estimateValueForItem } from './valueEstimatorRunner';
import { regressionVerdict, valueLabel, type GoldCase } from '../../supabase/functions/_shared/estimateValueCore.ts';

export const DEFAULT_GOLD_SET_SIZE = 8;

export interface RegressionCheck {
  cases: number;
  matchesWithout: number;
  matchesWith: number;
  passes: boolean;
}

/** Recent decisions a person accepted, with the value they accepted. */
export async function loadGoldSet(workspaceId: string, agentType: string, field: string | null, size: number): Promise<GoldCase[]> {
  let q = getSupabaseClient()
    .from('agent_decision_records')
    .select('object_id, field, proposed_value, review_value, review_outcome')
    .eq('workspace_id', workspaceId)
    .eq('agent_type', agentType)
    .in('review_outcome', ['accepted_as_proposed', 'accepted_with_change'])
    .order('reviewed_at', { ascending: false })
    .limit(size * 3);
  if (field) q = q.eq('field', field);
  const { data } = await q;
  const seen = new Set<string>();
  const out: GoldCase[] = [];
  for (const r of (data ?? []) as Array<{ object_id: string; field: string | null; proposed_value: unknown; review_value: unknown; review_outcome: string }>) {
    const key = `${r.object_id}|${r.field}`;
    if (!r.field || seen.has(key)) continue;
    seen.add(key);
    const accepted = valueLabel(r.review_outcome === 'accepted_with_change' ? r.review_value : (r.review_value ?? r.proposed_value));
    if (!accepted) continue;
    out.push({ itemId: r.object_id, field: r.field, acceptedValue: accepted });
    if (out.length >= size) break;
  }
  return out;
}

export async function checkRuleAgainstGoldSet(p: {
  workspaceId: string;
  agent: { agentType: string; name: string };
  settings: AgentSettings;
  ruleText: string;
  field: string | null;
}): Promise<RegressionCheck> {
  const size = Number((p.settings.learning as { goldSetSize?: number } | undefined)?.goldSetSize ?? DEFAULT_GOLD_SET_SIZE);
  const gold = await loadGoldSet(p.workspaceId, p.agent.agentType, p.field, size);
  if (!gold.length) return { cases: 0, matchesWithout: 0, matchesWith: 0, passes: true };
  const rules = await loadLearnedRules(p.workspaceId, p.agent.agentType);
  const without: Record<string, string | null> = {};
  const withRule: Record<string, string | null> = {};
  for (const g of gold) {
    const current = approvedRuleTexts(rules, g.field);
    const key = `${g.itemId}|${g.field}`;
    const a = await estimateValueForItem({ agent: p.agent, workspaceId: p.workspaceId, itemId: g.itemId, attributeKey: g.field, settings: p.settings, rules, preview: { rules: current } });
    const b = await estimateValueForItem({ agent: p.agent, workspaceId: p.workspaceId, itemId: g.itemId, attributeKey: g.field, settings: p.settings, rules, preview: { rules: [...current, p.ruleText] } });
    without[key] = a.outcome === 'preview' ? a.value : null;
    withRule[key] = b.outcome === 'preview' ? b.value : null;
  }
  return regressionVerdict(gold, without, withRule);
}
